import {
  type LessonItem,
  type ViewContext,
  addDays,
  isQuizOpen,
  shortName,
  todayIso,
  useAnnouncements,
  useAuth,
  useCatchUpPlan,
  useCourses,
  useLessons,
  useQuizAttempts,
  useQuizzes,
  useStudentHomework,
} from '@shkola/api';
import type { ToneName } from '@shkola/design';
import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useMinute } from '../../lib/now';

/*
 * What the student's home is built from (UX of 26.10, docs/ux-home-student.md): the day it shows (today
 * until its last lesson ends, then the next school day) and one list of what to do, by date.
 */

export type StudentContext = Extract<ViewContext, { kind: 'student' }>;

const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export const weekdayKey = (iso: string) =>
  WEEKDAY_KEYS[((new Date(`${iso}T00:00:00Z`).getUTCDay() + 6) % 7) as 0];

export const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const LONG = new Intl.DateTimeFormat('uk-UA', {
  timeZone: 'UTC',
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});
const DAY_MONTH = new Intl.DateTimeFormat('uk-UA', {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'short',
});
const DAY_MONTH_LONG = new Intl.DateTimeFormat('uk-UA', {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'long',
});
const WEEKDAY_SHORT = new Intl.DateTimeFormat('uk-UA', { timeZone: 'UTC', weekday: 'short' });
const MONTH = new Intl.DateTimeFormat('uk-UA', { timeZone: 'UTC', month: 'long' });
const at = (iso: string) => new Date(`${iso}T12:00:00Z`);

/** «Понеділок, 2 листопада» */
export const dayLong = (iso: string) => capitalize(LONG.format(at(iso)));
/** «2 листопада» */
export const dayMonthLong = (iso: string) => DAY_MONTH_LONG.format(at(iso));
/** «28 жовт.» */
export const dayMonthShort = (iso: string) => DAY_MONTH.format(at(iso));
/** «ср» */
export const weekdayShort = (iso: string) => WEEKDAY_SHORT.format(at(iso));
/** «Листопад» */
export const monthName = (iso: string) => capitalize(MONTH.format(at(iso)));
/** ISO weekday, 1 = Monday */
export const isoWeekday = (iso: string) => ((new Date(`${iso}T00:00:00Z`).getUTCDay() + 6) % 7) + 1;

export const roomOf = (t: TFunction, room: string | null) =>
  room ? (/^\d/.test(room) ? t('schedule.room', { room }) : room) : '';

/** «каб. 203 · Коваленко В. Р.» — the substitute's name when there is one. */
export const metaOf = (t: TFunction, l: LessonItem) => {
  const teacher = l.substitute ?? l.teachers[0];
  return [roomOf(t, l.room), teacher && shortName(teacher)].filter(Boolean).join(' · ');
};

/** «📌 фартух» → «фартух»: things to pack are words here, without the emoji. */
export const plain = (s: string) =>
  s
    .replace(/[\p{Extended_Pictographic}\u{FE0F}\u{200D}]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();

export const minutesBetween = (from: number, to: number) => Math.round((to - from) / 60_000);

/**
 * The colour of a level, one code everywhere (12-point scale of МОН): high — green, good — blue,
 * middle — orange, low — red; a thematic or term grade — violet.
 */
export const LEVEL_TONES: ReadonlyArray<ToneName> = ['red', 'orange', 'blue', 'green'];

/** The student's lessons of the next two weeks: one query for the day and for the tests to prepare. */
function useStudentLessons(ctx: StudentContext) {
  const studentId = ctx.child.memberId;
  const scope = useMemo(() => ({ kind: 'student' as const, studentId }), [studentId]);
  const today = todayIso();
  return useLessons(ctx.schoolId, scope, today, addDays(today, 14));
}

/**
 * The day the home shows: today while its lessons go on, then the next school day (after the last
 * lesson, on a weekend, in the holidays).
 */
export function useShownDay(ctx: StudentContext) {
  const now = useMinute();
  const today = todayIso();
  const lessons = useStudentLessons(ctx);
  const all = lessons.data ?? [];
  const todays = all.filter((l) => l.date === today);
  const last = todays.filter((l) => l.status !== 'cancelled').at(-1);
  const goingOn = last !== undefined && now < Date.parse(last.endsAt);
  const next = all.find((l) => l.date > today && l.status !== 'cancelled')?.date;
  const date = goingOn ? today : next;
  return {
    pending: lessons.isPending,
    today,
    /** today's lessons, also when the home already shows the next day */
    todays,
    date,
    isToday: date === today,
    lessons: date ? all.filter((l) => l.date === date) : [],
  };
}

/** Slots of a day: the lessons and the breaks between them, in order. */
export type Slot =
  | { kind: 'lesson'; lesson: LessonItem; start: number; end: number }
  | { kind: 'break'; start: number; end: number; minutes: number };

export function slotsOf(lessons: ReadonlyArray<LessonItem>): Slot[] {
  const sorted = [...lessons].sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  const slots: Slot[] = [];
  sorted.forEach((lesson, i) => {
    const start = Date.parse(lesson.startsAt);
    const end = Date.parse(lesson.endsAt);
    const prev = sorted[i - 1];
    if (prev) {
      const prevEnd = Date.parse(prev.endsAt);
      if (start > prevEnd)
        slots.push({
          kind: 'break',
          start: prevEnd,
          end: start,
          minutes: minutesBetween(prevEnd, start),
        });
    }
    slots.push({ kind: 'lesson', lesson, start, end });
  });
  return slots;
}

/** Where today stands: the lesson going on, the next one, before the first, after the last. */
export function dayNow(lessons: ReadonlyArray<LessonItem>, now: number) {
  const held = lessons
    .filter((l) => l.status !== 'cancelled')
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  const live = held.find((l) => Date.parse(l.startsAt) <= now && now < Date.parse(l.endsAt));
  const next = held.find((l) => Date.parse(l.startsAt) > now);
  const first = held[0];
  const last = held.at(-1);
  const before = first !== undefined && now < Date.parse(first.startsAt);
  const over = last !== undefined && now >= Date.parse(last.endsAt);
  return {
    held,
    live,
    next,
    first,
    last,
    before,
    over,
    inBreak: !live && !before && !over && next !== undefined,
  };
}

// —— what to do ——

export type TodoGroup = 'overdue' | 'today' | 'tomorrow' | 'week' | 'rework' | 'ack' | 'catchUp';
export const TODO_GROUPS: ReadonlyArray<TodoGroup> = [
  'overdue',
  'today',
  'tomorrow',
  'week',
  'rework',
  'ack',
  'catchUp',
];

export type TodoLink =
  | { to: '/homework'; course?: string }
  | { to: '/quizzes' }
  | { to: '/schedule' }
  | { to: '/announcements' }
  | { to: '/catch-up' };

export interface TodoItem {
  key: string;
  group: TodoGroup;
  kind: 'homework' | 'reading' | 'quiz' | 'test' | 'rework' | 'ack' | 'catchUp';
  title: string;
  subject?: string;
  /** the date it is due (a test — the date of the lesson) */
  due?: string;
  /** a test's time, «12:25» */
  time?: string;
  /** a test's topic, the teacher's words on a returned work */
  note?: string | null;
  /** how many missed lessons, for «наздогнати» */
  count?: number;
  /** the subjects of the missed lessons */
  subjects?: string[];
  link: TodoLink;
}

/**
 * Everything the student has to do, in one list by date (refs: Classroom's «To-do», Canvas's list):
 * homework and online tests not handed in, tests at lessons to prepare for (a week ahead), works the
 * teacher returned, announcements to confirm, missed lessons to catch up. An item leaves the list when it
 * is done — never because it was seen.
 */
export function useTodo(ctx: StudentContext) {
  const now = useMinute();
  const studentId = ctx.child.memberId;
  const today = todayIso();
  const tomorrow = addDays(today, 1);
  const weekEnd = addDays(today, 7);
  const { me } = useAuth();
  const memberIds = (me?.memberships ?? [])
    .filter((m) => m.school_id === ctx.schoolId)
    .map((m) => m.member_id);
  const homework = useStudentHomework(studentId, addDays(today, -30));
  const lessons = useStudentLessons(ctx);
  const quizzes = useQuizzes({ schoolId: ctx.schoolId });
  const attempts = useQuizAttempts({ studentId });
  const courses = useCourses({ classId: ctx.child.class?.id ?? '' });
  const announcements = useAnnouncements(ctx.schoolId, memberIds);
  const catchUp = useCatchUpPlan(studentId);

  const byDate = (due: string): TodoGroup | undefined =>
    due < today
      ? 'overdue'
      : due === today
        ? 'today'
        : due === tomorrow
          ? 'tomorrow'
          : due <= weekEnd
            ? 'week'
            : undefined;

  const items: TodoItem[] = [];
  const data = homework.data;
  for (const h of data?.homework ?? []) {
    const answer = (data?.submissions ?? [])
      .filter((s) => s.homeworkId === h.id)
      .sort((a, b) => b.submittedAt.localeCompare(a.submittedAt))[0];
    if (answer?.status === 'returned') {
      items.push({
        key: `rework-${h.id}`,
        group: 'rework',
        kind: 'rework',
        title: h.title,
        subject: h.subject,
        due: h.dueOn,
        note: answer.feedback,
        link: { to: '/homework', course: h.courseId },
      });
      continue;
    }
    if (h.requiresSubmission) {
      if (answer) continue;
      const group = byDate(h.dueOn);
      if (group)
        items.push({
          key: `hw-${h.id}`,
          group,
          kind: 'homework',
          title: h.title,
          subject: h.subject,
          due: h.dueOn,
          link: { to: '/homework', course: h.courseId },
        });
      continue;
    }
    // to read or learn, nothing to hand in: only while it is ahead
    const group = h.dueOn >= today ? byDate(h.dueOn) : undefined;
    if (group)
      items.push({
        key: `read-${h.id}`,
        group,
        kind: 'reading',
        title: h.title,
        subject: h.subject,
        due: h.dueOn,
        link: { to: '/homework', course: h.courseId },
      });
  }

  const mine = new Set((courses.data ?? []).map((c) => c.id));
  const tried = new Set((attempts.data ?? []).map((a) => a.quizId));
  for (const q of quizzes.data ?? []) {
    if (!mine.has(q.courseId) || tried.has(q.id) || !isQuizOpen(q, today)) continue;
    const group = byDate(q.dueOn);
    if (group)
      items.push({
        key: `quiz-${q.id}`,
        group,
        kind: 'quiz',
        title: q.title,
        subject: q.subject,
        due: q.dueOn,
        link: { to: '/quizzes' },
      });
  }

  for (const l of lessons.data ?? []) {
    if (!l.isTest || l.status === 'cancelled' || l.date > weekEnd) continue;
    if (l.date === today && Date.parse(l.startsAt) <= now) continue;
    const group = byDate(l.date);
    if (group)
      items.push({
        key: `test-${l.id}`,
        group,
        kind: 'test',
        title: l.subject,
        due: l.date,
        time: l.startsAt,
        note: l.topic,
        link: { to: '/schedule' },
      });
  }

  for (const a of announcements.data ?? []) {
    if (a.isRecipient && a.requiresAck && !a.readAt)
      items.push({
        key: `ack-${a.id}`,
        group: 'ack',
        kind: 'ack',
        title: a.title,
        link: { to: '/announcements' },
      });
  }

  const missed = (catchUp.data ?? []).filter((c) => !c.done);
  if (missed.length > 0)
    items.push({
      key: 'catch-up',
      group: 'catchUp',
      kind: 'catchUp',
      title: '',
      count: missed.length,
      subjects: [...new Set(missed.map((c) => c.subject))],
      link: { to: '/catch-up' },
    });

  const order = (a: TodoItem, b: TodoItem) =>
    a.group === 'overdue'
      ? (b.due ?? '').localeCompare(a.due ?? '') // the latest missed first: still worth doing
      : (a.due ?? '').localeCompare(b.due ?? '') || (a.time ?? '').localeCompare(b.time ?? '');
  const groups = TODO_GROUPS.map((group) => ({
    group,
    items: items.filter((i) => i.group === group).sort(order),
  })).filter((g) => g.items.length > 0);

  return {
    pending: homework.isPending,
    today,
    tomorrow,
    groups,
    count: (group: TodoGroup) => groups.find((g) => g.group === group)?.items.length ?? 0,
    testsOn: (date: string) =>
      items.filter((i) => i.kind === 'test' && i.due === date).map((i) => i.title),
  };
}
